
import { toast } from 'sonner';
import { supabase } from '../../supabase';
import { MAX_FILE_SIZE, ALLOWED_MIME_TYPES, MAX_UPLOADS_PER_HOUR } from './constants';

// Helper function to validate file before upload
export function validatePaperFile(file: File): { valid: boolean; message: string } {
  if (!file) {
    return { valid: false, message: 'No file provided' };
  }
  
  // Check file type
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    return { valid: false, message: 'Invalid file type. Only PDF files are allowed.' };
  }
  
  // Check file extension
  const fileExt = file.name.split('.').pop()?.toLowerCase();
  if (fileExt !== 'pdf') {
    return { valid: false, message: 'Invalid file extension. Only .pdf files are allowed.' };
  }
  
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return { 
      valid: false, 
      message: `File too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB.` 
    };
  }
  
  return { valid: true, message: 'File is valid' };
}

// Check rate limits for a user based on AI reviews triggered
export async function checkUploadRateLimit(userId: string): Promise<boolean> {
  const hourAgo = new Date();
  hourAgo.setHours(hourAgo.getHours() - 1);
  
  // Get the bot user ID for AI reviews
  const { data: botData } = await supabase
    .from('bots')
    .select('id')
    .eq('name', 'PaperVerseBot')
    .maybeSingle();
  
  if (!botData?.id) {
    console.error('Could not find bot user for rate limit check');
    return true; // Allow upload if we can't verify
  }
  
  // Count AI reviews (by bot) for this user's papers in the last hour
  const { count, error: countError } = await supabase
    .from('reviews')
    .select('*, papers!inner(author_id)', { count: 'exact', head: true })
    .eq('reviewer_id', botData.id)
    .eq('papers.author_id', userId)
    .gt('created_at', hourAgo.toISOString());
  
  if (countError) {
    console.error('Error checking rate limit:', countError);
    return true;
  } 
  
  if (count && count >= MAX_UPLOADS_PER_HOUR) {
    toast.error('AI Review limit reached', {
      description: `You can only trigger ${MAX_UPLOADS_PER_HOUR} AI reviews per hour`
    });
    return false;
  }
  
  return true;
}

// Verify the storage bucket exists with improved checks
export async function verifyStorageBucket(): Promise<boolean> {
  try {
    // First try to directly access the bucket
    const { data: bucketFiles, error: accessError } = await supabase.storage
      .from('papers')
      .list('', { limit: 1 });
      
    if (!accessError) {
      // Bucket exists and we can access it
      return true;
    }
    
    // If direct access fails, try listing all buckets
    const { data: bucketList, error: bucketsError } = await supabase.storage.listBuckets();
    
    if (bucketsError) {
      console.error('Error listing storage buckets:', bucketsError);
      toast.error('Storage system error', {
        description: 'Could not verify storage buckets. Please try again later.'
      });
      return false;
    }
    
    const papersBucketExists = bucketList?.some(bucket => bucket.name === 'papers');
    
    if (!papersBucketExists) {
      console.error('Papers storage bucket does not exist:', bucketList);
      
      // Try to create the bucket on-demand
      try {
        const { error: createError } = await supabase.storage.createBucket('papers', {
          public: true,
          fileSizeLimit: 50 * 1024 * 1024 // 50MB
        });
        
        if (createError) {
          console.error('Error creating papers bucket on-demand:', createError);
          toast.error('Storage configuration error', {
            description: 'The storage system is not properly configured. Please contact support.'
          });
          return false;
        }
        
        console.log('Created papers bucket on-demand');
        return true;
        
      } catch (createErr) {
        console.error('Failed to create bucket on-demand:', createErr);
        toast.error('Storage configuration error', {
          description: 'The storage system is not properly configured. Please contact support.'
        });
        return false;
      }
    }
    
    return true;
  } catch (error) {
    console.error('Unexpected error verifying storage bucket:', error);
    toast.error('Storage system error', {
      description: 'An unexpected error occurred. Please try again later.'
    });
    return false;
  }
}
